/**
 * API呼び出し用のユーティリティ
 * 環境変数 REACT_APP_API_URL でバックエンドURLを設定可能
 * 未設定時: 開発は localhost:8000、本番は相対パス /api（Vercel rewrites でバックエンドへ転送）
 *
 * 本番（Vercel）でチャットが動くため: Vercel の Project Settings で REACT_APP_API_URL を
 * 未設定のままにすること。設定するとブラウザが Cloud Run に直アクセスし CORS で失敗しうる。
 */

// ビルド時のデフォルト。ブラウザでは別オリジンなら相対パスに切り替える（下記 getApiUrl）
const API_BASE_URL_RAW =
  process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
    ? process.env.REACT_APP_API_URL
    : process.env.NODE_ENV === 'development'
      ? 'http://localhost:8000'
      : '';

const isApiDebugEnabled =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_ENABLE_API_DEBUG_LOGS === 'true';

const debugLog = (...args) => {
  if (isApiDebugEnabled) {
    console.debug(...args);
  }
};

export function getAnalyticsHeaders() {
  try {
    const stored = sessionStorage.getItem('analytics_session');
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (!parsed?.id) return {};
    return { 'X-Session-Id': parsed.id };
  } catch (_) {
    return {};
  }
}

/**
 * APIエンドポイントのURLを生成。
 * ブラウザで「ページとAPIが別オリジン」のときは相対パスにし、Vercel の /api → Cloud Run 転送を使う（404/CORS 回避）。
 */
export const getApiUrl = (endpoint) => {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  let base = API_BASE_URL_RAW;
  // 本番でチャット・認証は必ず相対パスにし、Vercel の /api → Cloud Run 転送を使う（HTML が返って JSON パースで落ちるのを防ぐ）
  if (
    process.env.NODE_ENV === 'production' &&
    typeof endpoint === 'string' &&
    (endpoint.includes('gemini/chat') || endpoint.includes('api/auth'))
  ) {
    base = '';
  } else if (typeof window !== 'undefined' && base) {
    try {
      const pageOrigin = window.location.origin;
      const apiOrigin = new URL(base).origin;
      if (pageOrigin !== apiOrigin) {
        base = '';
      }
    } catch (_) {}
  }

  if (base) {
    if (endpoint.startsWith('/api')) return `${base}${endpoint}`;
    return `${base}/api${endpoint}`;
  }
  if (endpoint.startsWith('/api')) return endpoint;
  return `/api${endpoint}`;
};

const ADMIN_TOKEN_KEY = 'admin_auth_token';

export function getAdminAuthToken() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) : null;
  } catch (_) {
    return null;
  }
}

export function setAdminAuthToken(token) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (_) {}
}

/**
 * 管理API用ヘッダー。管理画面の ID/パスワードログインで発行したトークンを優先する。
 */
export function getAdminAuthHeaders(endpoint) {
  const ep = String(endpoint || '');
  if (ep.includes('/api/admin/login') || ep.includes('/api/admin/logout')) {
    return {};
  }
  const token = getAdminAuthToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function getAuthHeaders() {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

/**
 * API呼び出しのラッパー関数
 * @param {string} endpoint - APIエンドポイント
 * @param {object} options - fetchオプション
 * @returns {Promise<Response>}
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAnalyticsHeaders(),
      ...getAuthHeaders(),
      ...options.headers,
    },
  };
  
  const response = await fetch(url, { ...defaultOptions, ...options });
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  
  return response;
};

/**
 * GETリクエスト
 */
export const apiGet = async (endpoint, params = {}) => {
  const url = getApiUrl(endpoint);
  
  // URLSearchParamsは自動的にURLエンコードを行う
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      searchParams.append(key, params[key]);
    }
  });
  
  const queryString = searchParams.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  
  debugLog('API Request:', fullUrl);
  
  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAnalyticsHeaders(),
        ...getAuthHeaders(),
        ...getAdminAuthHeaders(endpoint),
      },
    });
    
    debugLog('API Response:', response.status, response.statusText, response.url);

    // 未ログインのセッション確認は 401 を想定どおり扱い、例外・console.error にしない
    if (!response.ok && response.status === 401 && String(endpoint).includes('/api/auth/me')) {
      return { user: null };
    }

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      const contentType = response.headers.get('content-type') || '';
      try {
        if (contentType.includes('application/json')) {
          const errorData = await response.json();
          if (errorData.detail) errorMessage = `${response.status}: ${errorData.detail}`;
        } else {
          const text = await response.text();
          if (text.trim().startsWith('<')) {
            errorMessage = `${response.status}: サーバーがHTMLを返しました。APIのURLまたはプロキシ設定を確認してください。`;
          }
        }
      } catch (_) {}
      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      if (text.trim().startsWith('<')) {
        throw new Error(
          'サーバーがHTMLを返しました。本番ではAPIが正しく転送されていない可能性があります。'
        );
      }
      throw new Error(`想定外のレスポンス形式: ${contentType}`);
    }
    return response.json();
  } catch (error) {
    // ネットワークエラーなどの場合
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network Error:', error);
      console.error('Request URL:', fullUrl);
      console.error('API_BASE_URL_RAW:', API_BASE_URL_RAW);
      console.error('NODE_ENV:', process.env.NODE_ENV);
      // より詳細なエラーメッセージ
      const errorMsg = `Network Error: Failed to fetch from ${fullUrl}. ` +
        `Please check: 1) Backend server is running on http://localhost:8000, ` +
        `2) CORS is configured correctly, 3) No firewall blocking the connection.`;
      throw new Error(errorMsg);
    }
    console.error('API Error:', error);
    throw error;
  }
};

/**
 * POSTリクエスト
 */
export const apiPost = async (endpoint, data = {}) => {
  const url = getApiUrl(endpoint);
  
  debugLog('API Request (POST):', url, data);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAnalyticsHeaders(),
        ...getAuthHeaders(),
        ...getAdminAuthHeaders(endpoint),
      },
      body: JSON.stringify(data),
    });
    
    debugLog('API Response (POST):', response.status, response.statusText, response.url);
    
    if (!response.ok) {
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      const ct = response.headers.get('content-type') || '';
      try {
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          if (errorData.detail) errorMessage = `${response.status}: ${errorData.detail}`;
        } else {
          const text = await response.text();
          if (text.trim().startsWith('<')) {
            errorMessage = `${response.status}: サーバーがHTMLを返しました。APIのURLまたはプロキシ設定を確認してください。`;
          }
        }
      } catch (_) {}
      throw new Error(errorMessage);
    }

    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const text = await response.text();
      if (text.trim().startsWith('<')) {
        throw new Error(
          'サーバーがHTMLを返しました。本番ではAPIが正しく転送されていない可能性があります。'
        );
      }
      throw new Error(`想定外のレスポンス形式: ${ct}`);
    }
    return response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network Error:', error);
      throw new Error('Network Error: Failed to fetch. Please check your internet connection and API URL configuration.');
    }
    console.error('API Error:', error);
    throw error;
  }
};

/**
 * PUTリクエスト
 */
export const apiPut = async (endpoint, data = {}) => {
  const url = getApiUrl(endpoint);

  try {
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAnalyticsHeaders(),
        ...getAuthHeaders(),
        ...getAdminAuthHeaders(endpoint),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      const ct = response.headers.get('content-type') || '';
      try {
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          if (errorData.detail) errorMessage = `${response.status}: ${errorData.detail}`;
        } else {
          const text = await response.text();
          if (text.trim().startsWith('<')) {
            errorMessage = `${response.status}: サーバーがHTMLを返しました。APIのURLまたはプロキシ（Vercel の /api 転送）を確認してください。`;
          }
        }
      } catch (_) {}
      throw new Error(errorMessage);
    }

    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const text = await response.text();
      if (text.trim().startsWith('<')) {
        throw new Error(
          'サーバーがHTMLを返しました。本番では frontend/vercel.json の /api 転送が効いていない可能性があります。'
        );
      }
      throw new Error(`想定外のレスポンス形式: ${ct}`);
    }
    return response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network Error:', error);
      throw new Error('Network Error: Failed to fetch. Please check your internet connection and API URL configuration.');
    }
    console.error('API Error:', error);
    throw error;
  }
};

/**
 * DELETEリクエスト
 */
export const apiDelete = async (endpoint, params = {}) => {
  const url = getApiUrl(endpoint);
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      searchParams.append(key, params[key]);
    }
  });
  const queryString = searchParams.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  const response = await fetch(fullUrl, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAnalyticsHeaders(), ...getAuthHeaders(), ...getAdminAuthHeaders(endpoint) },
  });
  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.detail) errorMessage = `${response.status}: ${errorData.detail}`;
    } catch (e) {}
    throw new Error(errorMessage);
  }
  try {
    return await response.json();
  } catch (e) {
    return {};
  }
};
